import { cssVarV2, tintV2 } from '@streamlain/theme/v2';

/**
 * Design contract of the settings dialog. Every settings stylesheet takes its
 * type scale, radii, spacing and colours from here, so the dialog keeps exactly
 * four font sizes and three radii.
 */
export const settingsFont = {
  title: '22px',
  section: '15px',
  body: '14px',
  meta: '12px',
} as const;

export const settingsLineHeight = {
  title: '30px',
  section: '24px',
  body: '22px',
  meta: '18px',
} as const;

export const settingsRadius = {
  control: '6px',
  card: '8px',
  dialog: '12px',
  pill: '999px',
} as const;

export const settingsSpace = {
  /** Hairline step: tight lists and optical offsets, never layout gaps. */
  hair: '2px',
  xxs: '4px',
  xs: '8px',
  sm: '12px',
  md: '16px',
  lg: '24px',
  xl: '32px',
} as const;

export const settingsSize = {
  control: '32px',
  controlSmall: '28px',
  rowMin: '56px',
  icon: '16px',
  // Checkboxes scale with the font size; this is the one size they take.
  checkbox: '20px',
  sidebar: '264px',
  readingColumn: '720px',
  select: '200px',
  rail: '168px',
  dialogWidth: '1200px',
  dialogHeight: '860px',
} as const;

export const settingsColor = {
  background: cssVarV2('layer/background/overlayPanel'),
  sidebar: cssVarV2('layer/background/appChrome'),
  /** Surface of a box that groups the rows of one section. */
  box: cssVarV2('layer/background/appChrome'),
  well: cssVarV2('layer/background/secondary'),
  surface: cssVarV2('layer/background/primary'),
  /** Stays white in both themes, so brand logos keep their colours. */
  plate: cssVarV2('layer/pureWhite'),
  hover: cssVarV2('layer/background/hoverOverlay'),
  selected: cssVarV2('layer/background/selected'),
  scrim: cssVarV2('layer/background/modal'),
  text: cssVarV2('text/primary'),
  textSecondary: cssVarV2('text/secondary'),
  textTertiary: cssVarV2('text/tertiary'),
  textDisabled: cssVarV2('text/disable'),
  placeholder: cssVarV2('text/placeholder'),
  link: cssVarV2('text/link'),
  icon: cssVarV2('icon/primary'),
  iconSecondary: cssVarV2('icon/secondary'),
  border: cssVarV2('layer/insideBorder/border'),
  controlBorder: cssVarV2('layer/insideBorder/blackBorder'),
  divider: cssVarV2('tab/divider/divider'),
  brand: cssVarV2('button/primary'),
  onBrand: cssVarV2('button/pureWhiteText'),
  danger: cssVarV2('status/error'),
  success: cssVarV2('status/success'),
  warning: cssVarV2('status/warning'),
  /** Hints are lightly tinted boxes without a line. */
  hintInfo: tintV2('brand/primary', 'hint'),
  hintWarning: tintV2('status/warning', 'hint'),
  hintError: tintV2('status/error', 'hint'),
  input: cssVarV2('input/background'),
  inputBorderActive: cssVarV2('input/border/active'),
  segment: cssVarV2('switch/switchBackground/background'),
  segmentButton: cssVarV2('switch/buttonBackground/active'),
  chipText: cssVarV2('chip/label/text'),
  chipBlue: cssVarV2('chip/label/blue'),
  chipGreen: cssVarV2('chip/label/green'),
  chipGrey: cssVarV2('chip/label/grey'),
  chipOrange: cssVarV2('chip/label/orange'),
  chipPurple: cssVarV2('chip/label/purple'),
  chipRed: cssVarV2('chip/label/red'),
  chipYellow: cssVarV2('chip/label/yellow'),
} as const;

export const settingsShadow = {
  raised: cssVarV2('shadow/level1'),
  focus: `0 0 0 2px ${cssVarV2('layer/insideBorder/primaryBorder')}`,
  /** For controls inside a scrolling list, where an outer ring is cut off. */
  focusInset: `inset 0 0 0 2px ${cssVarV2('layer/insideBorder/primaryBorder')}`,
} as const;

export const settingsMotion = {
  enter: '160ms cubic-bezier(0.2, 0, 0, 1)',
  move: '180ms cubic-bezier(0.2, 0, 0, 1)',
  quick: '120ms',
} as const;

export const settingsMobileQuery = 'screen and (max-width: 768px)';
export const settingsReducedMotionQuery = '(prefers-reduced-motion: reduce)';
